import { ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { PlanLimitError } from '../../features/plans/models/enforcement.model';
import { PlanLimitStateService } from '../../features/plans/services/plan-limit-state.service';
import { ExpertRole } from '../../models/expert-role.model';
import { SessionInfoDto } from '../../models/chat/chat-session.models';
import { ChatSource, CsvDocumentInfo, EvidenceStatus } from '../../models/chat.model';
import { ChatInputMode, ChatItem, ChatQueryUiResult } from '../../models/chat-ui.model';
import { ChatService } from '../../services/chat/api/chat.service';
import { CsvTaskPollingService } from '../../services/chat/api/csv-task-polling.service';
import { ChatCsvFlowService, ChatCsvSelection, ChatCsvSubmitEvent } from '../../services/chat/flow/chat-csv-flow.service';
import { ChatMessageFlowEvent, ChatMessageFlowService } from '../../services/chat/flow/chat-message-flow.service';
import { ChatSourceMapperService } from '../../services/chat/mapping/chat-source-mapper.service';
import { AnswerSourceDocument, ChatAnswerSourcesService, ParsedSourceSummary } from '../../services/chat/presentation/chat-answer-sources.service';
import { ChatActionBuilderService } from '../../services/chat/presentation/chat-action-builder.service';
import { ChatCursorHintService } from '../../services/chat/presentation/chat-cursor-hint.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import { ChatFeedbackPanelState, ChatFeedbackUiService } from '../../services/chat/presentation/chat-feedback-ui.service';
import { ChatIdService } from '../../services/chat/presentation/chat-id.service';
import { ChatMessageTextService } from '../../services/chat/presentation/chat-message-text.service';
import { ChatSessionLoaderService } from '../../services/chat/session/chat-session-loader.service';
import { ChatSessionUiService } from '../../services/chat/session/chat-session-ui.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { ExpertRolesService } from '../../services/expert-roles/expert-roles.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { GenerationService } from '../../services/generation/generation.service';
import { LanguageService } from '../../services/language/language.service';
import { NotificationService } from '../../services/notification.service';
import { TaskNavigationService } from '../../services/task-navigation.service';
import { UserService } from '../../services/user.service';
import { ChatLifecycleController } from './chat-lifecycle.controller';
import { ChatMessageController } from './chat-message.controller';
import { evidenceStatusLabelKey, evidenceWarningKey } from './chat-runtime.evidence';
import { ChatConversationMetadata, formatChatError } from './chat-runtime.models';
import { ChatQueryController } from './chat-query.controller';
import { ChatRequestTracker } from './chat-request.tracker';
import { ChatRoleComposerController } from './chat-role-composer.controller';
import { ChatRuntimeState } from './chat-runtime.state';
import { ChatSessionController } from './chat-session.controller';
import { ChatSourceController } from './chat-source.controller';

export interface ChatRuntimeDependencies {
  formBuilder: FormBuilder; chatService: ChatService; language: LanguageService;
  csvPolling: CsvTaskPollingService; logger: FrontendLoggerService;
  sessionUi: ChatSessionUiService; changeDetector: ChangeDetectorRef;
  actionBuilder: ChatActionBuilderService; messageText: ChatMessageTextService;
  sourceMapper: ChatSourceMapperService; storage: ChatStorageService;
  dom: ChatDomEffectsService; sessionLoader: ChatSessionLoaderService;
  messageFlow: ChatMessageFlowService; csvFlow: ChatCsvFlowService;
  feedback: ChatFeedbackUiService; ids: ChatIdService; router: Router;
  taskNavigation: TaskNavigationService; userService: UserService;
  planLimits: PlanLimitStateService; notifications: NotificationService;
  answerSources: ChatAnswerSourcesService; cursorHint: ChatCursorHintService;
  expertRoles: ExpertRolesService; generation: GenerationService;
}

export class ChatRuntimeController {
  readonly state = new ChatRuntimeState();
  private readonly roles: ChatRoleComposerController;
  private readonly requests: ChatRequestTracker;
  private readonly sessions: ChatSessionController;
  private readonly sources: ChatSourceController;
  private readonly queries: ChatQueryController;
  private readonly messageController: ChatMessageController;
  private readonly lifecycle: ChatLifecycleController;

  constructor(private readonly deps: ChatRuntimeDependencies) {
    const state = this.state;
    this.roles = new ChatRoleComposerController(
      state, deps.expertRoles, deps.storage, deps.feedback, deps.ids,
      deps.planLimits, deps.changeDetector
    );
    this.requests = new ChatRequestTracker(state, deps.logger, deps.changeDetector);
    this.sessions = new ChatSessionController(
      state, deps.chatService, deps.sessionLoader, deps.storage, deps.sessionUi,
      deps.dom, deps.feedback, deps.ids, deps.notifications, deps.language,
      deps.logger, deps.changeDetector, this.roles, this.requests
    );
    this.sources = new ChatSourceController(
      state, deps.answerSources, deps.csvFlow, deps.feedback, deps.storage,
      deps.dom, deps.logger, deps.changeDetector, this.sessions
    );
    this.queries = new ChatQueryController(
      state, deps.messageFlow, deps.chatService, deps.storage, deps.sourceMapper,
      deps.dom, deps.logger, deps.changeDetector, this.roles, this.requests, this.sessions
    );
    this.messageController = new ChatMessageController(
      state, deps.messageText, deps.taskNavigation, deps.router, deps.generation,
      deps.ids, deps.feedback, deps.storage, deps.dom, deps.cursorHint,
      deps.language, deps.logger, deps.changeDetector
    );
    this.lifecycle = new ChatLifecycleController(
      state, deps.formBuilder, deps.router, deps.storage, deps.csvPolling, deps.actionBuilder,
      deps.userService, deps.language, deps.logger, deps.dom, deps.changeDetector,
      this.roles, this.requests, this.sessions, this.messageController
    );
  }

  get armedGenerateSummary(): boolean { return this.state.armedGenerateSummary; }
  get chatForm(): FormGroup { return this.state.chatForm; }
  get expertRoles(): ExpertRole[] { return this.state.expertRoles; }
  get expertRolesLoading(): boolean { return this.state.expertRolesLoading; }
  get isLoading(): boolean { return this.state.isLoading; }
  get lastAppliedExpertRole(): string | null { return this.state.lastAppliedExpertRole; }
  get planLimitError(): PlanLimitError | null { return this.state.planLimitError; }
  get selectedExpertRole(): string | null { return this.state.selectedExpertRole; }
  get selectedInputMode(): ChatInputMode { return this.state.selectedInputMode; }
  get showToolsPanel(): boolean { return this.state.showToolsPanel; }
  set showToolsPanel(value: boolean) { this.state.showToolsPanel = value; }
  get copiedMessageIndex(): number | null { return this.state.copiedMessageIndex; }
  get error(): string | null { return this.state.error; }
  get feedbackComment(): string { return this.state.feedbackComment; }
  set feedbackComment(value: string) { this.state.feedbackComment = value; }
  get feedbackLike(): boolean | null { return this.state.feedbackLike; }
  get feedbackRating(): number { return this.state.feedbackRating; }
  get feedbackSent(): boolean { return this.state.feedbackSent; }
  get feedbackTargetIndex(): number | null { return this.state.feedbackTargetIndex; }
  get messages(): ChatItem[] { return this.state.messages; }
  get showFeedback(): boolean { return this.state.showFeedback; }
  get userAvatarUrl(): string | null { return this.state.userAvatarUrl; }
  get editingSessionId(): string | null { return this.state.editingSessionId; }
  get editingSessionName(): string { return this.state.editingSessionName; }
  set editingSessionName(value: string) { this.state.editingSessionName = value; }
  get selectedSessionId(): string | null { return this.state.selectedSessionId; }
  set selectedSessionId(value: string | null) { this.state.selectedSessionId = value; }
  get sessionList(): SessionInfoDto[] { return this.state.sessionList; }
  get sessionModalTitle(): string { return this.state.sessionModalTitle; }
  get sessionNameError(): boolean { return this.state.sessionNameError; }
  get showSessionModal(): boolean { return this.state.showSessionModal; }
  set showSessionModal(value: boolean) { this.state.showSessionModal = value; }
  get showSessionNameSuccess(): string | null { return this.state.showSessionNameSuccess; }
  get confirmContext(): boolean { return this.state.confirmContext; }
  get contextModalVisible(): boolean { return this.state.contextModalVisible; }
  get contextModalX(): number { return this.state.contextModalX; }
  get contextModalY(): number { return this.state.contextModalY; }
  get currentSources(): ChatSource[] { return this.state.currentSources; }
  get selectedSource(): ChatSource | null { return this.state.selectedSource; }
  get showDetails(): boolean { return this.state.showDetails; }
  get showDownloadedMessage(): boolean { return this.state.showDownloadedMessage; }
  get limitedDocId(): string | null { return this.state.limitedDocId; }
  get limitedDocName(): string | null { return this.state.limitedDocName; }
  get mobileChatOptionsOpen(): boolean { return this.state.mobileChatOptionsOpen; }
  set mobileChatOptionsOpen(value: boolean) { this.state.mobileChatOptionsOpen = value; }
  get topK(): number { return this.state.topK; }
  set topK(value: number) { this.state.topK = value; }
  get isRequesting(): boolean { return this.state.isRequesting; }
  get showCsvModal(): boolean { return this.state.showCsvModal; }
  get pendingQuery(): string { return this.state.pendingQuery; }
  get csvDocuments(): CsvDocumentInfo[] { return this.state.csvDocuments; }

  initialize(): void { this.lifecycle.initialize(); }
  destroy(): void { this.lifecycle.destroy(); }
  translate(key: string): string { return this.deps.language.translate(key); }
  formatError(error: unknown): string { return formatChatError(error); }
  normalizeMessageText(html: string): string { return this.messageController.normalize(html); }
  displayMessageContent(message: ChatItem): string { return this.messageController.display(message); }
  shouldRenderMessageAsHtml(message: ChatItem): boolean { return this.messageController.shouldRenderHtml(message); }
  openTaskAction(id: string, event?: Event): void { this.messageController.openTask(id, event); }
  openRouteAction(action: any, event?: Event): void { this.messageController.openRoute(action, event); }
  openGenerateAction(action: any, event?: Event): void { this.messageController.openGenerate(action, event); }
  onChatClicked(event: MouseEvent): void { this.messageController.onChatClicked(event); }
  editMessageToInput(message: { content: string }): void {
    const control = this.state.chatForm.get('messageInput');
    if (control) this.messageController.editToInput(message, control);
  }
  copyMessage(message: Pick<ChatItem, 'content'>, index: number): Promise<void> {
    return this.messageController.copy(message, index);
  }
  toggleFeedbackForMessage(index: number, like: boolean): void { this.sources.toggleFeedback(index, like); }
  closeInlineFeedback(): void { this.sources.closeFeedback(); }
  onBotMessageHintEnter(message: ChatItem | null | undefined, event: MouseEvent): void {
    this.messageController.showCursorHint(message, event);
  }
  onBotMessageHintLeave(): void { this.messageController.hideCursorHint(); }
  selectMessage(message: ChatItem): void { this.messageController.selectMessage(message, () => this.getSelectedChunks()); }
  isMessageSelected(message: ChatItem | null | undefined): boolean { return this.messageController.isSelected(message); }
  deselectMessage(event?: MouseEvent): void { this.messageController.deselect(event); }

  // --- Fase 4: helpers de vista para el estado de evidencia (evidence_status) ---
  // La lógica pura vive en `./chat-runtime.evidence`; estos métodos preservan la API de vista.
  /** Llave i18n para la etiqueta del estado de evidencia. `unknown` si no es conocido. */
  evidenceStatusLabelKey(status: EvidenceStatus | null | undefined): string {
    return evidenceStatusLabelKey(status);
  }

  /** Llave i18n de la advertencia del estado de evidencia, o `null` si no amerita advertir. */
  evidenceWarningKey(status: EvidenceStatus | null | undefined): string | null {
    return evidenceWarningKey(status);
  }

  get isExpertRoleSelectorVisible(): boolean { return this.roles.isSelectorVisible; }
  get isLastExpertRoleDegraded(): boolean { return this.roles.isLastRoleDegraded; }
  get selectedExpertRoleDisplayName(): string | null { return this.roles.selectedDisplayName; }
  get selectedExpertRoleIcon(): string { return this.roles.selectedIcon; }
  get defaultExpertRole(): string | null { return this.roles.defaultRole; }
  get isCurrentModeBlocked(): boolean { return this.roles.isCurrentModeBlocked; }
  closeRoleDropdown(): void { this.roles.closeDropdown(); }
  onRoleOptionSelected(code: string | null): void { this.roles.selectRole(code); this.roles.closeDropdown(); }
  onEscapeCloseRoleDropdown(): void { this.roles.closeDropdown(); }
  selectExpertRole(code: string | null): void { this.roles.selectRole(code); }
  roleIcon(role: ExpertRole | null | undefined): string { return this.roles.roleIcon(role); }
  hasExpertRoleBadge(message: ChatItem): boolean { return this.roles.hasBadge(message); }
  expertRoleBadge(slug: string): { icon: string; name: string } { return this.roles.badge(slug); }
  capitalizeSlug(slug: string): string { return this.roles.capitalizeSlug(slug); }
  loadExpertRoles(): void { this.roles.load(this.state.destroy$); }
  restoreSelectedExpertRole(): void { this.roles.restore(); }
  clearSelectedExpertRole(): void { this.roles.clear(); }

  autoGrowTextarea(target: EventTarget | null): void { this.roles.autoGrow(target); }
  onChatKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault(); this.submitChat();
    }
  }
  submitChat(override?: string): void {
    this.sendMessage(override);
    setTimeout(() => { if (this.state.chatTextarea) this.state.chatTextarea.nativeElement.style.height = 'auto'; });
  }
  generateSummary(): void {
    this.state.armedGenerateSummary = true; this.state.showToolsPanel = false;
    setTimeout(() => this.state.chatTextarea?.nativeElement?.focus());
  }
  cancelGenerateSummary(): void { this.state.armedGenerateSummary = false; }
  selectInputMode(mode: ChatInputMode): void { this.roles.selectInputMode(mode); }
  toggleToolsPanel(): void {
    this.state.showToolsPanel = !this.state.showToolsPanel;
    if (this.state.showToolsPanel) this.roles.closeDropdown();
  }
  getStarColor(star: number): string { return this.roles.getStarColor(star); }
  getStarClass(star: number): string { return this.roles.getStarClass(star); }
  setHoverRating(value: number): void { this.state.hoverRating = value; }
  setFeedbackRating(value: number): void { this.state.feedbackRating = value; }
  setTopK(value: number): void { this.state.topK = value; }
  generateMessageEntityId(): string { return this.roles.generateMessageEntityId(); }
  startRequest(): void { this.requests.start(); }
  endRequest(): void { this.requests.end(); }
  endRequestIfStuck(): void { this.requests.endIfStuck(); }

  sendMessage(override?: string): void { this.queries.send(override); }
  applyMessageFlowEvent(event: ChatMessageFlowEvent): void { this.queries.applyFlowEvent(event); }
  applyQueryUiResult(result: ChatQueryUiResult): void { this.queries.applyQueryResult(result); }
  parseConversationMetadata(value: unknown): ChatConversationMetadata { return this.queries.parseMetadata(value); }
  getSelectedChunks(): void { this.queries.loadSelectedChunks(); }
  toggleDetails(): void { this.queries.toggleDetails(); }

  selectSource(source: ChatSource): void { this.sources.select(source); }
  parseSourceSummary(source: ChatSource | null | undefined): ParsedSourceSummary | null { return this.sources.parseSummary(source); }
  get selectedSourceSummary(): ParsedSourceSummary | null { return this.sources.selectedSummary; }
  getAnswerSourceDocuments(message: ChatItem | null | undefined): AnswerSourceDocument[] { return this.sources.answerDocuments(message); }
  downloadAnswerDocument(id: string, event?: MouseEvent): void { this.sources.downloadDocument(id, event); }
  onSourceDownloadClick(event: MouseEvent, source: ChatSource | null): void { this.sources.onDownloadClick(event, source); }
  getSourceDownloadUrl(source: ChatSource | null): string | null { return this.sources.downloadUrl(source); }
  onSourceRightClick(event: MouseEvent, source: ChatSource): void { this.sources.onRightClick(event, source); }
  readonly trackBySource = (index: number, source: ChatSource): string | number =>
    this.sources.trackBy(index, source);
  clearSource(): void { this.sources.clear(); }
  sendFeedback(liked: boolean | null): void { this.sources.sendFeedback(liked); }
  applyFeedbackPanelState(state: ChatFeedbackPanelState): void { this.sources.applyFeedback(state); }
  scrollToBottom(): void { this.sessions.scrollToBottom(); }
  onCsvCancel(): void { this.sources.cancelCsv(); }
  onCsvSubmit(data: ChatCsvSelection): void { this.sources.submitCsv(data); }
  applyCsvSubmitEvent(event: ChatCsvSubmitEvent): void { this.sources.applyCsvEvent(event); }
  openContextModalForSource(source: ChatSource, event?: MouseEvent): void { this.sources.openContext(source, event); }
  beginContextQuestion(): void { this.state.confirmContext = true; }
  confirmContextSelection(value: boolean): void { this.sources.confirmContextSelection(value); }

  applyInitialSessionLoadResult(result: any): void { this.sessions.applyInitial(result); }
  applySelectedSessionLoadResult(result: any): void { this.sessions.applySelected(result); }
  clearChatHistory(): void { this.sessions.clearHistory(); }
  onSessionSelected(id: string): void { this.sessions.selectSession(id, () => this.generateMessageEntityId()); }
  onSessionRecover(): void { this.sessions.recoverSession(() => this.generateMessageEntityId()); }
  onNewSession(): void { this.sessions.newSession(); }
  onCloseEvent(): void { this.sessions.closeModal(); }
  onClearLimitedDoc(): void { this.sessions.clearLimitedDocument(); }
  dismissPlanLimit(): void { this.state.planLimitError = null; }
  showPlanLimit(error: PlanLimitError): void { this.sessions.showPlanLimit(error); }
  updateButtonText(text: string): void { this.state.buttonText = text; }
  changeName(session: SessionInfoDto): void { this.sessions.changeName(session); }
  saveSessionName(session: SessionInfoDto): void { this.sessions.saveName(session); }
  cancelEditSessionName(): void { this.sessions.cancelNameEdit(); }
  deriveSessionTitle(text: string): string { return this.sessions.deriveTitle(text); }
  maybeAutoTitleSession(id: string | null): void { this.sessions.maybeAutoTitle(id); }
}
